import { DeleteOutline, HideImage } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from "react-i18next";
import { trimObject } from "utils/utils";

/** @type {imagePickerConfig} */
const DEFAULT_CONFIG = {
  objectFit: "contain",
  allowedMimeTypes: ["image/jpeg", "image/png"],
  maxSize: 2 * 1024 * 1024, // 2MB
  dimension: {},
}

const styles = {
  mandatoryAsterisk: {
    color: "red",
  },
  descriptionText: {
    color: "grey",
  },
  hintText: {
    color: "red",
  },
  deleteButton: {
    backgroundColor: "#36bfba",
    "&:hover": {
      backgroundColor: "#dcf1f1",
    },
  },
  container: {
    marginBottom: "18px",
  },
  uploadBox: {
    width: 150,
    height: 150,
    border: "2px dashed",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    overflow: "hidden",
    position: "relative",

    borderColor: "divider",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "primary.main",
    },
  },
  uploadBoxError: {
    borderColor: "error.main",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "error.main",
    },
  },
  uploadBoxDisabled: {
    cursor: "not-allowed",
    backgroundColor: "action.hover",
    "&:hover": {
      borderColor: "divider !important",
    },
  },
  uploadBoxReadonly: {
    cursor: "auto",
    border: "2px solid",
    "&:hover": {
      backgroundColor: "unset",
      borderColor: "divider !important",
    },
  },
  uploadText: {
    mt: 1,
    color: "text.secondary",
  },
  uploadTextError: {
    mt: 1,
    color: "error.main",
  },
  helperMessageContainer: {
    flexDirection: "row",
  },
};

/**
 * @typedef {"image/jpeg" | "image/png"} supportedMimeTypes
 */

/**
 * @typedef {Object} dimensionConfig
 * @property {[number, number]} widthLimit - [minimum, maximum] leave empty for no limit
 * @property {[number, number]} heightLimit - [minimum, maximum] leave empty for no limit
 */

/**
 * @typedef {Object} imagePickerConfig
 * @property {import("react").CSSProperties["objectFit"]} [objectFit]
 * @property {(supportedMimeTypes)[]} allowedMimeTypes
 * @property {number} maxSize
 * @property {dimensionConfig?} [dimension]
 */

/** @type {Record<supportedMimeTypes, string>} */
const MIME_TYPE_ERROR_MAP = {
  "image/jpeg": "JPG",
  "image/png": "PNG",
}

/**
 * @param {Object} props 
 * @param {import("react-hook-form").RefCallBack?} props.formBuilderFocusRef
 * @param {string?} props.currentImage - base64String value of current image
 * @param {string?} props.defaultImage - base64String value of default image
 * @param {(base64String: string) => void} props.onChange 
 * @param {(base64String: string) => void} props.onInit - first parameter is the base64 string of the default image
 * @param {(errorMessage: string) => void} props.onError 
 * @param {boolean} props.disabled 
 * @param {boolean} props.readOnly
 * @param {string?} props.errorMessage
 * @param {boolean?} props.noErrorMessage
 * @param {imagePickerConfig} props.config
 */
const ImagePicker = ({
  formBuilderFocusRef,
  currentImage,
  defaultImage,
  onChange,
  onError,
  disabled,
  readOnly,
  errorMessage,
  noErrorMessage = false,
  config,
}) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: 'component.form.imagePicker' });
  // const { t: tCommon } = useTranslation();
  const [image, setImage] = useState(null);
  const inputElementRef = useRef();

  const parsedConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...trimObject(config),
    ...{
      dimension: {
        ...DEFAULT_CONFIG.dimension,
        ...trimObject(config.dimension),
      },
    }
  }), [config]);

  const supportedMimeTypes = useMemo(() =>
    parsedConfig.allowedMimeTypes.map((type) => MIME_TYPE_ERROR_MAP[type]).join("/"),
    [parsedConfig.allowedMimeTypes]
  );

  const handleFileChange = (e) => {
    /** @type {File} */
    const file = e.target.files?.[0];

    const readFile = () => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
        const signature = reader?.result?.split(",")[1];
        let isValid = false;
        parsedConfig.allowedMimeTypes.forEach((type) => {
          switch (type) {
            case "image/jpeg":
              if (signature?.startsWith("/9j/")) {
                isValid = true
              }
              break;
            case "image/png":
              if (signature?.startsWith("iVBORw0KGgo")) {
                isValid = true
              }
              break;
          }
        })
        if (isValid) {
          onChange(base64Image);
          setImage(base64Image);
        } else {
          onError(tLocal("error.invalidFileType", { mimeTypes: supportedMimeTypes }));
        }
      }

      reader.readAsDataURL(file);
    }

    if (!file) {
      return;
    }

    if (!parsedConfig.allowedMimeTypes.includes(file.type)) {
      onError(tLocal("error.invalidFileType", { mimeTypes: supportedMimeTypes }));
      return;
    }

    if (file.size > parsedConfig.maxSize) {
      onError(tLocal("error.invalidFileSize", { size: parsedConfig.maxSize / 1024 / 1024 }));
      return;
    }

    if (!parsedConfig.dimension) {
      readFile();
    } else {
      const img = new Image();

      img.onload = () => {
        const { width, height } = img;
        const { widthLimit, heightLimit } = parsedConfig.dimension;

        const isWidthInvalid = widthLimit && (width < widthLimit[0] || width > widthLimit[1]);
        const isHeightInvalid = heightLimit && (height < heightLimit[0] || height > heightLimit[1]);
        if (isWidthInvalid || isHeightInvalid) {
          onError(tLocal("error.invalidImageDimension", {
            minWidth: widthLimit[0],
            minHeight: heightLimit[0],
            maxWidth: widthLimit[1],
            maxHeight: heightLimit[1],
          }));
          URL.revokeObjectURL(img.src);
          return
        }
        URL.revokeObjectURL(img.src);
        readFile();
      };

      img.src = URL.createObjectURL(file);
    }
  }

  useEffect(() => {
    if (defaultImage && !currentImage) {
      setImage(defaultImage);
      onChange(currentImage);
    }
  }, [defaultImage]);

  useEffect(() => {
    if (currentImage !== image) {
      setImage(currentImage);
      onChange(currentImage);
    }
  }, [currentImage]);

  return (
    <Box sx={{ textAlign: "center", position: "relative" }}>
      <Stack gap={2} direction={"row"} alignItems={"center"}>
        <Box
          onClick={() => inputElementRef.current.click()}
          sx={{ position: "relative" }}>
          {formBuilderFocusRef && <input
            // Mock input element, special ref for formBuilderImagePicker to jump to this component
            ref={formBuilderFocusRef}
            style={{
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              border: 0,
              whiteSpace: "nowrap",     // helps with some screen reader cases
            }}
            tabIndex={-1}
          />}
          <input
            ref={inputElementRef}
            type="file"
            accept={parsedConfig.allowedMimeTypes?.join(", ")}
            value={""} //force uncontrolled to prevent bug
            onChange={handleFileChange}
            disabled={disabled || readOnly}
            style={{ display: "none" }}
          />
          <Box sx={{
            ...styles.uploadBox,
            ...errorMessage && styles.uploadBoxError,
            ...disabled && styles.uploadBoxDisabled,
            ...readOnly && styles.uploadBoxReadonly
          }}>
            {image ? (
              <img
                key={image}
                src={image}
                style={{ width: "100%", height: "100%", objectFit: parsedConfig.objectFit }}
              />
            ) : (
              <>
                {(!disabled && !readOnly) ?
                  <>
                    <Typography
                      variant="body2"
                      sx={
                        errorMessage ? styles.uploadTextError : styles.uploadText
                      }
                    >
                      {tLocal("label.uploadImage")}
                    </Typography>
                  </>
                  :
                  <HideImage
                    fontSize="large"
                    color={errorMessage ? "error" : "action"}
                  />
                }
              </>
            )}
          </Box>
        </Box>

        {image && !disabled && !readOnly && (
          // Delete button
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              onChange(null);
              setImage(null);
            }}
            sx={styles.deleteButton}
            disabled={disabled || readOnly}
          >
            <DeleteOutline sx={{ color: "white" }} fontSize="medium" />
          </IconButton>
        )}
      </Stack>
      {!noErrorMessage && <Stack sx={styles.helperMessageContainer}>
        {errorMessage && <HelperMessage message={errorMessage} />}
      </Stack>}
    </Box>
  )
}

export default ImagePicker